import React from "react";
import { Page, Text, View } from "@react-pdf/renderer";
import { DateTime } from "luxon";
import tools from "../../../../helpers/tools";
import {
  MAIN_REASONS,
  SANCTION_TYPES,
} from "../../RecipientDetail/sanctionConstants";

const getReasonLabel = (reasonId) => {
  const reason = Object.values(MAIN_REASONS).find((r) => r.id === reasonId);
  return reason?.label || reasonId || "—";
};

const getSanctionTypeLabel = (type) => {
  if (type === SANCTION_TYPES.SUSPENSION) return "Suspension";
  if (type === SANCTION_TYPES.SUPPRESSION) return "Suppression";
  return type || "—";
};

const getDetectionLabel = (type) => {
  const labels = {
    PREMIER: "1er manquement",
    PERSISTANCE: "Persistance",
    REITERATION: "Réitération",
  };
  return labels[type] || type || "";
};

const getPresenceLabel = (presenceId) => {
  const labels = {
    PRESENT: "Présent",
    PRESENT_ACCOMPAGNE: "Présent (accompagné)",
    ABSENT: "Absent",
    ABSENT_EXCUSE: "Absent excusé",
    ABSENT_NON_EXCUSE: "Absent non excusé",
    NON_CONVOQUE: "Non convoqué",
  };
  return labels[presenceId] || presenceId || "—";
};

const getDirectorDecisionLabel = (decision) => {
  if (decision === "ACCORD") return "Accord";
  if (decision === "A_SURSEOIR") return "À surseoir";
  return decision || "—";
};

const colConfig = [
  { name: "Allocataire", path: "name", size: "large" },
  { name: "Type", path: "type", size: "small" },
  { name: "Motif principal", path: "reason", size: "large" },
  { name: "Référent unique", path: "ru", size: "medium" },
  { name: "Présence", path: "presence", size: "small" },
  { name: "Décision EP", path: "decisionEP", size: "large" },
  { name: "Décision directeur", path: "decisionDirector", size: "medium" },
];

const LARGE_COL_PCT = 20;
const MEDIUM_COL_PCT = 12;
const nbLarge = colConfig.filter((c) => c.size === "large").length;
const nbMedium = colConfig.filter((c) => c.size === "medium").length;
const nbSmall = colConfig.filter((c) => c.size === "small").length;
const totalFixed = LARGE_COL_PCT * nbLarge + MEDIUM_COL_PCT * nbMedium;
const smallPct = (100 - totalFixed) / nbSmall;

const getWidth = (size) => {
  if (size === "large") return `${LARGE_COL_PCT}%`;
  if (size === "medium") return `${MEDIUM_COL_PCT}%`;
  return `${smallPct}%`;
};

const getName = (rec) => tools.getFullname(rec) || rec.fullname || "—";

const PVAnnexesV2 = ({ styles, ep, inscriptions, title }) => {
  // Tri : suppressions en premier (ordre alpha), puis suspensions (ordre alpha).
  // On attribue un rang numérique par type (SUPPRESSION=0, SUSPENSION=1).
  // Si les deux inscriptions sont du même type, on trie par nom d'allocataire.
  // Les types inconnus tombent en dernier (rang 2).
  const sorted = [...inscriptions].sort((a, b) => {
    const typeOrder = { SUPPRESSION: 0, SUSPENSION: 1 };
    const ta = typeOrder[a.type] ?? 2;
    const tb = typeOrder[b.type] ?? 2;
    if (ta !== tb) return ta - tb;
    return getName(a.userRecipient || {}).localeCompare(
      getName(b.userRecipient || {}),
    );
  });

  return (
    <Page wrap size="A4" orientation="landscape">
      {/* Header */}
      <View style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 20 }}>
        <View style={{ fontSize: 12, textAlign: "center" }}>{title}</View>
        <Text style={{ ...styles.title }}>
          Liste des allocataires et décisions
        </Text>
      </View>

      {/* Column headers */}
      <View
        style={{
          ...styles.tableRowCustom,
          paddingLeft: 20,
          paddingRight: 20,
        }}
      >
        {colConfig.map((col, k) => (
          <Text
            key={`hd${k}`}
            style={{
              ...styles.tableCell17,
              ...styles.tableHead,
              width: getWidth(col.size),
            }}
          >
            {col.name}
          </Text>
        ))}
      </View>

      {/* Rows */}
      {sorted.map((ins, k) => {
        const rec = ins.userRecipient || {};
        const dirDecision = ins.directorDecision;
        const dirNote = ins.directorNote;
        const decisionText = ins.decisionText || "—";

        return (
          <View
            key={`row${k}`}
            style={{
              ...styles.tableRowCustom,
              paddingLeft: 20,
              paddingRight: 20,
            }}
            wrap={false}
          >
            {/* Allocataire : nom + NIR + N°CAF + date naiss. */}
            <View
              style={{
                ...styles.tableCell17,
                width: getWidth("large"),
              }}
            >
              <Text style={{ fontWeight: "bold" }}>{getName(rec)}</Text>
              {rec.nir && (
                <Text style={{ fontSize: 7, color: "#555", marginTop: 1 }}>
                  NIR : {rec.nir}
                </Text>
              )}
              {rec.CAFId && (
                <Text style={{ fontSize: 7, color: "#555", marginTop: 1 }}>
                  N°CAF : {rec.CAFId}
                </Text>
              )}
              {rec.birthdate && (
                <Text style={{ fontSize: 7, color: "#555", marginTop: 1 }}>
                  Né(e) le{" "}
                  {DateTime.fromISO(rec.birthdate).toFormat("dd/MM/yyyy")}
                </Text>
              )}
            </View>

            {/* Type / Détection */}
            <View
              style={{
                ...styles.tableCell17,
                width: getWidth("small"),
              }}
            >
              <Text>{getSanctionTypeLabel(ins.type)}</Text>
              {ins.detectionType && (
                <Text style={{ fontSize: 7, color: "#555", marginTop: 1 }}>
                  {getDetectionLabel(ins.detectionType)}
                </Text>
              )}
            </View>

            {/* Motif principal uniquement (pas les modérés) */}
            <View
              style={{
                ...styles.tableCell17,
                width: getWidth("large"),
              }}
            >
              <Text>{getReasonLabel(ins.motif)}</Text>
            </View>

            {/* Référent unique (organisme de saisine) */}
            <Text
              style={{
                ...styles.tableCell17,
                width: getWidth("medium"),
              }}
            >
              {ins.organizationOrigin || "—"}
            </Text>

            {/* Présence */}
            <Text
              style={{
                ...styles.tableCell17,
                width: getWidth("small"),
              }}
            >
              {getPresenceLabel(ins.presenceId)}
            </Text>

            {/* Décision EP */}
            <View
              style={{
                ...styles.tableCell17,
                width: getWidth("large"),
              }}
            >
              <Text>{decisionText}</Text>
              {ins.radiationBRSA && (
                <Text style={{ fontSize: 7, marginTop: 2 }}>
                  + Radiation de la liste des BRSA
                </Text>
              )}
              {ins.radiationDE && (
                <Text style={{ fontSize: 7, marginTop: 2 }}>
                  + Radiation DE
                </Text>
              )}
            </View>

            {/* Décision directeur */}
            <View
              style={{
                ...styles.tableCell17,
                width: getWidth("medium"),
              }}
            >
              <Text
                style={{
                  color:
                    dirDecision === "ACCORD"
                      ? "green"
                      : dirDecision === "A_SURSEOIR"
                        ? "#b45309"
                        : "black",
                  fontWeight: "bold",
                }}
              >
                {getDirectorDecisionLabel(dirDecision)}
              </Text>
              {dirNote && (
                <Text style={{ fontSize: 7, marginTop: 2, color: "#555" }}>
                  {dirNote}
                </Text>
              )}
            </View>
          </View>
        );
      })}
    </Page>
  );
};

export default PVAnnexesV2;
